import { App, Workouts } from "app-interfaces";
import i18next from "i18next";
import React from "react";
import { v4 as uuid } from "uuid";

import {
  isScoreTypeWithIntegerValues,
  metricConverter,
  useStateValue,
} from "../../config/AppContext";
import { ScoreType, ScoreTypeName } from "../../types/StaticTypes";
import enumToArray from "../Utilities/enumToArray";
import {
  ExerciseInputsData,
  getScoreInput,
  getTabInputsData,
} from "./AddSetsOfExercises/TabComponent";
import TabsList from "./AddSetsOfExercises/TabsList";

export default function AddSetsOfExercises(props: {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  supersetList?: Workouts.IExercise[] | null;
  addExercise?: Workouts.IExercise[] | null;
  children?: JSX.Element | JSX.Element[];
}) {
  const { metric } = useStateValue() as { metric: App.State["metric"] };

  const initialTab = 0;
  const [addExercise, setAddExercise] = React.useState(props.addExercise);
  const [supersetList, setSupersetList] = React.useState(props.supersetList);
  const [currentTab, dispatch] = React.useReducer(reduceCurrentTab, initialTab);

  function handleChange(form: HTMLFormElement) {
    const formData = new FormData(form);
    const newExerciseList = formData.get("exercises");
    if (newExerciseList && typeof newExerciseList === "string") {
      if (supersetList) return setSupersetList(JSON.parse(newExerciseList));
      if (addExercise) return setAddExercise(JSON.parse(newExerciseList));
    }
  }

  function reduceCurrentTab(tab: number, action: { type: string; payload?: any }) {
    const form = document.getElementById("step-form") as HTMLFormElement | null;
    if (!form) return tab;
    switch (action.type) {
      case "reset":
        handleChange(form);
        break;
      case "invalid":
        return tab;
      case "prev":
        if (tab === initialTab) return initialTab;
        return tab - 1;
      case "next":
        if (tab === initialTab) {
          handleChange(form);
          return tab + 1;
        }
        return tab + 1;
      case "submit":
        handleChange(form);
        form.dispatchEvent(new Event("submit"));
        break;
      default:
        throw new Error();
    }
    return initialTab;
  }

  /**
   * If score value from an input is valid, based on score type:
   * some must be integers (e.g. "repetitions"), some can be decimal numbers.
   */
  const isValidScoreValue = (scoreTypeName: ScoreTypeName, scoreValue: Number) =>
    isScoreTypeWithIntegerValues(scoreTypeName)
      ? Number.isInteger(scoreValue)
      : !Number.isNaN(scoreValue);

  /**
   * Get all score types used for exercise set, for example:
   * ["LENGTH", "PAUSE", "SPEED", "WATT"]
   */
  const getExerciseSetScoreTypes = (inputsData: ExerciseInputsData) => {
    // All possible score types
    const scoreTypes = enumToArray(ScoreType);

    return Object.keys(inputsData).filter((fieldName) =>
      scoreTypes.includes(fieldName)
    ) as ScoreTypeName[];
  };

  /**
   * Create the "score_vals" array that we need to send to backend,
   * based on the inputsData and score types that the user used.
   */
  const getScoreVals = (
    scoreCols: ScoreTypeName[],
    inputsData: ExerciseInputsData
  ): Workouts.IExercise["score_vals"] => {
    return scoreCols.map((scoreCol) => ({
      type: scoreCol,
      value: inputsData[scoreCol].map((value) => Number(value)),
    }));
  };

  /**
   * Number of sets the user added for this exercise.
   * We need to send it to backend as "score_count" field.
   */
  const getScoreCount = (
    scoreVals: Workouts.IExercise["score_vals"]
  ): Workouts.IExercise["score_count"] => scoreVals[0].value.length;

  /**
   * Convert a score number user entered, based on score type and units this user uses.
   */
  const convertScoreUnits = (scoreValue: number, scoreType: string) =>
    metricConverter(scoreValue, `${scoreType}O`, metric);

  /**
   * Convert score numbers that user specified, based on units this user uses.
   */
  const covnvertAllScoreUnits = (scoreVals: Workouts.IExercise["score_vals"]) =>
    scoreVals.map((scoreValItem) => ({
      ...scoreValItem,
      value: scoreValItem.value.map((scoreValue) =>
        convertScoreUnits(scoreValue, scoreValItem.type)
      ),
    }));

  /**
   * Validate score values the user entered for each score type, for each exercise set.
   * Toggle input's "is-invalid" class directly, as Table.tsx uses pure JS.
   */
  const validateScoreVals = (scoreVals: Workouts.IExercise["score_vals"]) => {
    let isFormValid = true;
    scoreVals.forEach((scoreValItem) => {
      scoreValItem.value.forEach((scoreValue, scoreColumnIndex) => {
        const isValid = isValidScoreValue(scoreValItem.type, scoreValue);
        const scoreInput = getScoreInput(currentTab, scoreValItem.type, scoreColumnIndex);
        if (!isValid) {
          isFormValid = false;
          scoreInput.classList.add("is-invalid");
        } else {
          scoreInput.classList.remove("is-invalid");
        }
      });
    });
    return isFormValid;
  };

  // Handle Prev, Next / Submit button action:
  const handleAction = (e: React.MouseEvent) => {
    const action = e.currentTarget.id;
    const form = document.getElementById("step-form") as HTMLFormElement | null;
    const tabs = [...document.getElementsByClassName("tab")] as HTMLElement[] | null;

    if (!action || !form || !tabs || !tabs[currentTab]) return;

    const formData = new FormData(form);
    const exercises = formData.get("exercises");
    if (typeof exercises === "string" && JSON.parse(exercises).length === 0)
      return dispatch({ type: "reset" });

    switch (action) {
      case "prevBtn":
        if (currentTab === 1) {
          const stepsIndicator = document.getElementById("setMarginTop");
          if (stepsIndicator && window.innerWidth > 700) {
            stepsIndicator.style.top = "unset";
          }
        }
        return dispatch({ type: "prev" });
      case "nextBtn":
        // The first tab shows the list of exercises to select from (unlike all the other tabs),
        // and it saves them to the <input id="data">.
        // That's why we don't do much here for the first tab, but have a totally different code for
        // the rest of the tabs.
        if (currentTab === 0) {
          return dispatch({ type: "next" });
        }
        // All the other tabs, apart from the first one, are for adding sets for a particular exercise,
        // so here we have logic to save the sets user specified for a particular (current) exercise.
        else {
          // Get current exercise id:
          const exercise_id =
            (supersetList &&
              supersetList[currentTab - 1] &&
              supersetList[currentTab - 1].exercise_id) ||
            (addExercise && addExercise[currentTab - 1] && addExercise[currentTab - 1].exercise_id);

          // Collect input fields:
          const tabInputs = [...tabs[currentTab].querySelectorAll("input.form-control")] as
            | HTMLInputElement[]
            | null;
          // Get target input field:
          const dataInput = document.getElementById("data") as HTMLInputElement | null;

          if (!exercise_id || !dataInput || !tabInputs) return;

          if (tabInputs.length === 1) {
            const scoreTy = document.getElementsByName("score_type")[0] as HTMLSelectElement;
            const x = document.getElementById(`set-table-${currentTab}`) as HTMLTableElement;
            // ?.rows[1].cells[0].innerHTML
            let arr = scoreTy.selectedIndex ? ["REPS"] : ["TIME"];
            for (let i = 1; i < x.rows.length; i++) {
              if (i === 1) {
              } else {
                let j = x.rows[i].cells[0].children[0].innerHTML.split("<");
                // console.log('j', j[0])
                if (j[0] !== "results") {
                  arr.push(j[0].toUpperCase());
                }
              }
            }
            const currentExerciseList = JSON.parse(dataInput.value) as Workouts.IExercise[];
            let exerciseNotes;
            if (tabInputs[0].name === "exercise_notes")
              exerciseNotes = tabInputs[0].getAttribute("value");
            const newExerciseItem = {
              id: uuid(),
              exercise_id,
              score_count: 0,
              score_type: `${scoreTy.selectedIndex ? "REPS" : "TIME"}`,
              score_cols: arr,
              score_vals: [],
              result_vals: [],
              notes_sets: [],
              exercise_notes: exerciseNotes || null,
            };
            Object.assign(currentExerciseList[currentTab - 1], newExerciseItem);
            // Update data input value:
            dataInput.value = JSON.stringify(currentExerciseList);
          } else {
            const tabInputsData = getTabInputsData(currentTab);
            const scoreCols = getExerciseSetScoreTypes(tabInputsData);
            const scoreVals = getScoreVals(scoreCols, tabInputsData);

            if (!validateScoreVals(scoreVals)) {
              return;
            }

            const convertedScoreValues = covnvertAllScoreUnits(scoreVals);

            const currentExerciseSets: Workouts.IExercise = {
              id: uuid(),
              exercise_id,
              score_type: tabInputsData.score_type[0],
              score_count: getScoreCount(scoreVals),
              score_cols: scoreCols,
              score_vals: convertedScoreValues,
              result_vals: [],
              notes_sets: tabInputsData.notes_sets,
              exercise_notes: tabInputsData.exercise_notes[0],
            };

            const currentExerciseList = JSON.parse(dataInput.value) as Workouts.IExercise[];
            Object.assign(currentExerciseList[currentTab - 1], currentExerciseSets);
            // Update data input value:
            dataInput.value = JSON.stringify(currentExerciseList);
          }

          // If on the last tab then submit form:
          if (tabs.length > 1 && currentTab === tabs.length - 1) {
            return dispatch({ type: "submit", payload: dataInput.value });
          }
          // ... else move to next tab:
          return dispatch({ type: "next" });
        }
      default:
        return dispatch({ type: "reset" });
    }
  };

  // Handle current tab
  const showTab = React.useCallback(() => {
    const tabs = document.getElementsByClassName("tab") as HTMLCollectionOf<HTMLElement> | null;
    const prevBtn = document.getElementById("prevBtn") as HTMLButtonElement | null;
    const nextBtn = document.getElementById("nextBtn") as HTMLButtonElement | null;

    if (!tabs || !tabs[currentTab] || !prevBtn || !nextBtn) return;
    const temp = document.getElementById("superset");
    // const temp2= document.getElementById("tablist-container")
    // console.log(temp2?.children[1].className)
    //  const oldClass=temp2?.children[1].className
    if (currentTab) {
      if (temp) {
        // temp2?.children[1].setAttribute("class", `${oldClass} scroller`)
        window.innerWidth > 700 && temp.children[2].setAttribute("style", "width: 87vw");
        // console.log(temp)
      }
    } else {
      if (temp) {
        window.innerWidth > 700 && temp.children[2].setAttribute("style", "width: 512px");
        // temp2?.children[1].setAttribute("class", `tab`)
      }
    }
    // Handle step indicator:
    const fixStepIndicator = (n: number) => {
      // Remove class from all steps:
      const steps = document.getElementsByClassName(
        "step"
      ) as HTMLCollectionOf<HTMLSpanElement> | null;
      if (!steps || !steps[n]) return;
      [...steps].forEach((v, i) => {
        steps[i].className = steps[i].className.replace(" active", "");
      });
      // If on first tab remove finish class:
      if (n === 0) steps[n].className = steps[n].className.replace(" finish", "");
      // ... else add finish class to previous step:
      if (n !== 0) steps[n - 1].className += " finish";
      // ... add active class to current step:
      steps[n].className = steps[n].className.replace(" finish", "");
      steps[n].className += " active";
    };

    // Hide all tabs
    for (let tab of tabs) {
      tab.style.display = "none";
    }
    // Display the current tab:
    tabs[currentTab].style.display = "block";

    let currentTabTabs = tabs[currentTab].children as HTMLCollectionOf<HTMLElement>;

    currentTabTabs.length > 1 && (currentTabTabs[0].style.width = "unset");
    currentTabTabs.length > 1 && (currentTabTabs[0].style.paddingRight = "0px");
    currentTabTabs.length > 1 && (currentTabTabs[1].style.width = "unset");
    currentTabTabs.length > 1 && (currentTabTabs[1].style.marginTop = "0px");

    // Fix previous button:
    if (currentTab === 0) {
      prevBtn.style.display = "none";
    } else {
      prevBtn.style.display = "inline";
    }

    // Fix next button:
    if (currentTab === tabs.length - 1) {
      if (currentTab === 0) nextBtn.innerHTML = i18next.t("Next");
      else nextBtn.innerHTML = i18next.t("Save");
    } else {
      nextBtn.innerHTML = i18next.t("Next");
    }
    // Fix step indicators:
    fixStepIndicator(currentTab);
  }, [currentTab]);

  // Effects
  React.useEffect(showTab, [showTab, currentTab]);

  return (
    <form id="step-form" className="step-form" onSubmit={props.onSubmit}>
      {/* Tabs section */}
      {props.children}
      <TabsList
        currentTab={currentTab}
        addExercise={addExercise}
        supersetList={supersetList}
        handleAction={handleAction}
      />
    </form>
  );
}
